import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

const QUERY_TIMEOUT_MS = 8000;
const REST_BASE_URL = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1`;
const REST_HEADERS: HeadersInit = {
  apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
  Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
};

export interface Category {
  id: string;
  name: string;
  image: string | null;
  slug: string;
  display_size?: string;
  show_in_menu?: boolean;
  show_in_shop_by_category?: boolean;
  show_on_home?: boolean;
}

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number): Promise<T> =>
  Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      globalThis.setTimeout(() => reject(new Error("categories_timeout")), timeoutMs);
    }),
  ]);

// ★ Single shared fetch — always loads ALL categories with ALL needed columns once.
// Per-section hooks below filter from this shared cache, eliminating duplicate network requests.
const fetchAllCategoriesShared = async (): Promise<Category[]> => {
  try {
    const url = new URL(`${REST_BASE_URL}/categories`);
    url.searchParams.set(
      "select",
      "id,name,image,slug,display_size,show_in_menu,show_in_shop_by_category,show_on_home",
    );
    url.searchParams.set("order", "sort_order.asc");

    const response = await withTimeout(
      fetch(url.toString(), { headers: REST_HEADERS }),
      QUERY_TIMEOUT_MS,
    );

    if (!response.ok) throw new Error(`categories_http_${response.status}`);
    return (await response.json()) as Category[];
  } catch (error) {
    console.error("categories load failed", error);
    return [];
  }
};

const SHARED_KEY = ["categories", "shared-all"];
const SHARED_OPTS = {
  queryKey: SHARED_KEY,
  queryFn: fetchAllCategoriesShared,
  staleTime: 5 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
};

/** Base hook returning ALL categories (single shared fetch). */
export const useAllCategoriesBase = (enabled = true) => {
  return useQuery({ ...SHARED_OPTS, enabled });
};

/** Menu categories — filtered from shared cache. */
export const useMenuCategories = (enabled = true) => {
  const query = useQuery({ ...SHARED_OPTS, enabled });
  const data = useMemo(
    () => (query.data || []).filter((c) => c.show_in_menu !== false),
    [query.data],
  );
  return { ...query, data };
};

/** Shop-by-category — filtered from shared cache. */
export const useShopCategories = (enabled = true, _compact = false) => {
  const query = useQuery({ ...SHARED_OPTS, enabled });
  const data = useMemo(
    () => (query.data || []).filter((c) => c.show_in_shop_by_category !== false),
    [query.data],
  );
  return { ...query, data };
};

/** Home categories — filtered from shared cache. */
export const useHomeCategories = (enabled = true) => {
  const query = useQuery({ ...SHARED_OPTS, enabled });
  const data = useMemo(
    () => (query.data || []).filter((c) => c.show_on_home !== false),
    [query.data],
  );
  return { ...query, data };
};
